import React, { useEffect, useState } from 'react';
import mammoth from 'mammoth';
import '../../styles/reading/RightSection.css';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { ConnectionState } from 'livekit-client';
import NebulaOrb from './NebulaOrb';
import { QUIZ_CONFIG, SESSION_SECONDS, buildQuizSchedule } from '../../config/quizConfig';

const QUIZ_SCHEDULE = buildQuizSchedule();

// Configure PDF.js worker - use local worker file from public folder
// This avoids CDN issues and works offline
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const RightSection = ({ content, file, topicUrl, startTimer, onAutoQuizTrigger, onManualQuizTrigger, onExtraQuiz, quizOpen = false, quizMode = 'timed', avatarOverlayLarge, liveKitRoomRef, liveKitConnectionState, isMicrophoneMuted, onToggleMute, onPageChange }) => {
  const isEmpty = (!content || content.trim() === '') && !file && !topicUrl;
  
  // Check if the topicUrl is a PDF
  const isPDF = topicUrl && topicUrl.toLowerCase().endsWith('.pdf');
  
  const [filePreview, setFilePreview] = useState(null);
  const [fileText, setFileText] = useState('');
  const [timeLeft, setTimeLeft] = useState(SESSION_SECONDS);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [showQuizPopup, setShowQuizPopup] = useState(false);
  const [showQuizWarning, setShowQuizWarning] = useState(false);  // ← NEW
 const [upcomingQuizNumber, setUpcomingQuizNumber] = useState(null);  // 1-based quiz number (regular quizzes + Final)
  const [showQuizPrepMessage, setShowQuizPrepMessage] = useState(false);  // Message 5 seconds before quiz
  const [showMusicPopup, setShowMusicPopup] = useState(false);
  const [selectedMusic, setSelectedMusic] = useState(null);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [pdfZoom, setPdfZoom] = useState(1.8); // PDF zoom level set to 180% for better readability
  const [numPages, setNumPages] = useState(null); // Total number of pages
  const [pageNumber, setPageNumber] = useState(1); // Current page number
  const [pdfError, setPdfError] = useState(null); // Error handling
  const [pdfLoading, setPdfLoading] = useState(true); // Loading state

  const backgroundMusic = [
    { 
      id: 1, 
      name: 'Sound of Rain', 
      icon: '🌧️', 
      description: 'Gentle rainfall sounds',
      url: '/Background music/Rain.wav'
    },
    { 
      id: 2, 
      name: 'Sound of River', 
      icon: '🏞️', 
      description: 'Peaceful flowing water',
      url: '/Background music/River.wav'
    },
    { 
      id: 3, 
      name: 'Forest Sounds', 
      icon: '🌲', 
      description: 'Birds chirping and rustling leaves',
      url: '/Background music/Forest.wav'
    },
    { 
      id: 4, 
      name: 'Ocean Waves', 
      icon: '🌊', 
      description: 'Calming wave sounds',
      url: '/Background music/Waves .wav'
    },
    { 
      id: 5, 
      name: 'White Noise', 
      icon: '⚪', 
      description: 'Steady background noise',
      url: '/Background music/White Noise.mp3'
    }
  ];

  useEffect(() => {
    if (!file) {
      setFilePreview(null);
      setFileText('');
      return;
    }
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setFilePreview(url);
      setFileText('');
      return () => URL.revokeObjectURL(url);
    } else if (
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.name.endsWith('.docx')
    ) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const arrayBuffer = e.target.result;
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setFileText(result.value);
      };
      reader.readAsArrayBuffer(file);
      setFilePreview(null);
    } else if (file.type.startsWith('text/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFileText(e.target.result);
      reader.readAsText(file);
      setFilePreview(null);
    } else {
      setFilePreview(null);
      setFileText('');
    }
  }, [file]);

  // Timer effect — the session countdown runs in BOTH modes once the user
  // clicks Start (isTimerActive). The auto-quiz triggers below only fire in
  // 'timed' mode; in 'manual' mode the clock still counts down but quizzes are
  // fired by the "Quiz Me" button instead.
  useEffect(() => {
    let interval = null;
    if (isTimerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft => {
          const newTime = timeLeft - 1;

          // Auto-quiz scheduling only applies in 'timed' mode.
          if (quizMode === 'timed') {
            const totalElapsed = SESSION_SECONDS - newTime;
            for (const slot of QUIZ_SCHEDULE) {
              // Show preparation message shortly before each quiz.
              if (totalElapsed === slot.prepAt) { setShowQuizPrepMessage(true); }
              // Forced quiz opening.
              if (totalElapsed === slot.triggerAt && onAutoQuizTrigger) {
                setShowQuizPrepMessage(false);
                onAutoQuizTrigger(slot.quizIndex);
              }
            }
          }

          return newTime;
        });
      }, 1000);
    } else if (timeLeft === 0) {
      setIsTimerActive(false);
      // Session over (in timed mode the Final Quiz fires on the last slot)
    }
    return () => clearInterval(interval);
  }, [isTimerActive, timeLeft, quizMode]);

  // Effect to handle automatic timer start from START button
  useEffect(() => {
    if (startTimer) {
      setIsTimerActive(true);
      if (timeLeft === 0) {
        setTimeLeft(SESSION_SECONDS);
      }
    }
  }, [startTimer, timeLeft]);

  // Seconds until the next scheduled quiz (null once the Final has fired); the Extra
  // Quiz button is locked during the last few seconds before each scheduled quiz.
  const elapsedSec = SESSION_SECONDS - timeLeft;
  const nextSlot = QUIZ_SCHEDULE.find(slot => slot.triggerAt > elapsedSec);
  const nextScheduledInSec = nextSlot ? nextSlot.triggerAt - elapsedSec : null;
  const extraQuizLocked = nextScheduledInSec !== null && nextScheduledInSec <= QUIZ_CONFIG.extraQuiz.lockBeforeScheduledSec;

  // Auto-close quiz preparation message after 5 seconds
  useEffect(() => {
    if (showQuizPrepMessage) {
      const timer = setTimeout(() => {
        setShowQuizPrepMessage(false);
      }, 5000); // Close after 5 seconds
      
      return () => clearTimeout(timer);
    }
  }, [showQuizPrepMessage]);


  const handleVibesClick = () => {
    setShowMusicPopup(true);
  };

  const selectMusic = (music) => {
    // Stop current audio if playing
    if (currentAudio) {
      // Disable looping before stopping
      currentAudio._shouldLoop = false;
      currentAudio.pause();
      currentAudio.currentTime = 0;
      // Remove all loop event listeners
      if (currentAudio._timeUpdateHandler) {
        currentAudio.removeEventListener('timeupdate', currentAudio._timeUpdateHandler);
      }
      if (currentAudio._endedHandler) {
        currentAudio.removeEventListener('ended', currentAudio._endedHandler);
      }
    }

    // Create new audio instance
    const audio = new Audio(music.url);
    audio.loop = true; // Loop the background music
    audio.volume = 0.3; // Set moderate volume
    
    // Set up audio event listeners
    audio.addEventListener('loadstart', () => {
      console.log('Loading audio...');
    });
    
    audio.addEventListener('canplay', () => {
      console.log('Audio ready to play');
    });
    
    audio.addEventListener('error', (e) => {
      console.error('Audio error:', e);
      // Silently handle error - user can try another track
    });

    // Ensure seamless looping without any gap - continuous playback until paused/stopped
    // Store music ID on audio object to check if it's still active
    audio._musicId = music.id;
    audio._shouldLoop = true; // Flag to control looping
    audio._isRestarting = false; // Prevent multiple simultaneous restarts
    
    // Continuous loop handler - ensures music never stops until paused/stopped
    const continuousLoopHandler = () => {
      // Only process if looping is enabled and not already restarting
      if (!audio._shouldLoop || audio._isRestarting || audio.paused) return;
      
      // Check if we're very close to the end (within 0.15 seconds for seamless transition)
      // This creates a "while loop" effect - continuously restarting until stopped
      if (audio.duration && audio.duration > 0 && audio.currentTime >= audio.duration - 0.15) {
        audio._isRestarting = true;
        // Immediately restart from beginning - zero gap
        audio.currentTime = 0;
        // The play() call ensures continuous playback
        audio.play().catch(error => {
          console.error('Error in continuous loop:', error);
          audio._isRestarting = false;
        });
        // Reset flag after brief moment
        setTimeout(() => {
          audio._isRestarting = false;
        }, 100);
      }
    };
    
    // Backup handler for ended event (handles edge cases)
    const endedHandler = () => {
      // Continuous loop: restart immediately if still should loop
      if (audio && audio._shouldLoop && audio._musicId === music.id && !audio._isRestarting && !audio.paused) {
        audio._isRestarting = true;
        audio.currentTime = 0;
        audio.play().catch(error => {
          console.error('Error in ended handler loop:', error);
          audio._isRestarting = false;
        });
        setTimeout(() => {
          audio._isRestarting = false;
        }, 100);
      }
    };
    
    // Use timeupdate for seamless looping (fires frequently during playback)
    // This creates the "while loop" effect - continuously monitoring and restarting
    audio.addEventListener('timeupdate', continuousLoopHandler);
    // Backup: handle ended event in case timeupdate misses it
    audio.addEventListener('ended', endedHandler);
    
    // Store handler references for cleanup
    audio._timeUpdateHandler = continuousLoopHandler;
    audio._endedHandler = endedHandler;

    // Start playing
    audio.play().then(() => {
      setCurrentAudio(audio);
      setSelectedMusic(music);
      setIsPlaying(true);
      setShowMusicPopup(false);
    }).catch(error => {
      console.error('Error playing audio:', error);
      // Silently handle error - user can try another track
      setShowMusicPopup(false);
    });
  };

  const toggleAudio = () => {
    if (currentAudio) {
      if (isPlaying) {
        currentAudio.pause();
        setIsPlaying(false);
      } else {
        currentAudio.play().then(() => {
          setIsPlaying(true);
        }).catch(error => {
          console.error('Error resuming audio:', error);
        });
      }
    }
  };

  const stopAudio = () => {
    if (currentAudio) {
      // Disable looping before stopping
      currentAudio._shouldLoop = false;
      currentAudio.pause();
      currentAudio.currentTime = 0;
      // Remove all loop event listeners
      if (currentAudio._timeUpdateHandler) {
        currentAudio.removeEventListener('timeupdate', currentAudio._timeUpdateHandler);
      }
      if (currentAudio._endedHandler) {
        currentAudio.removeEventListener('ended', currentAudio._endedHandler);
      }
      setCurrentAudio(null);
      setSelectedMusic(null);
      setIsPlaying(false);
    }
  };

  // Cleanup audio on component unmount
  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio._shouldLoop = false;
        currentAudio.pause();
        // Remove all loop event listeners
        if (currentAudio._timeUpdateHandler) {
          currentAudio.removeEventListener('timeupdate', currentAudio._timeUpdateHandler);
        }
        if (currentAudio._endedHandler) {
          currentAudio.removeEventListener('ended', currentAudio._endedHandler);
        }
        currentAudio.src = '';
      }
    };
  }, [currentAudio]);

  const closeMusicPopup = () => {
    setShowMusicPopup(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const closeQuizPopup = () => {
    setShowQuizPopup(false);
  };

  const closeQuizWarning = () => {
    setShowQuizWarning(false);
  };

  // PDF loading handlers
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfLoading(false);
    setPdfError(null);
    console.log(`PDF loaded successfully: ${numPages} pages`);
  };

  const onDocumentLoadError = (error) => {
    setPdfError(error);
    setPdfLoading(false);
    console.error('❌ PDF Loading Error:', error);
    console.error('Error details:', {
      message: error?.message,
      name: error?.name,
      stack: error?.stack,
      topicUrl: topicUrl
    });
  };

  const goToPrevPage = () => {
    setPageNumber(prev => {
      const newPage = Math.max(1, prev - 1);
      if (onPageChange && newPage !== prev) onPageChange(newPage);
      return newPage;
    });
  };

  const goToNextPage = () => {
    setPageNumber(prev => {
      const newPage = Math.min(numPages || 1, prev + 1);
      if (onPageChange && newPage !== prev) onPageChange(newPage);
      return newPage;
    });
  };

  // Reset page number when PDF URL changes
  useEffect(() => {
    if (isPDF && topicUrl) {
      setPageNumber(1);
      setPdfLoading(true);
      setPdfError(null);
      setNumPages(null);
    }
  }, [topicUrl, isPDF]);


  return (
    <div className="reading-right-section">
      <div className="content-layout">
        <div className="content-display large">
          {topicUrl ? (
            isPDF ? (
              <div style={{ 
                position: 'relative', 
                width: '100%', 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}>
                {/* Zoom Controls */}
                <div style={{ 
                  position: 'absolute', 
                  top: '10px', 
                  right: '10px', 
                  zIndex: 1000,
                  background: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '8px',
                  padding: '8px',
                  display: 'flex',
                  gap: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}>
                  <button
                    onClick={() => setPdfZoom(Math.max(0.5, pdfZoom - 0.2))}
                    disabled={pdfZoom <= 0.5}
                    style={{
                      padding: '4px 12px',
                      background: pdfZoom <= 0.5 ? '#ccc' : '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: pdfZoom <= 0.5 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    −
                  </button>
                  <span style={{ padding: '4px 8px', minWidth: '50px', textAlign: 'center', fontSize: '14px' }}>
                    {Math.round(pdfZoom * 100)}%
                  </span>
                  <button
                    onClick={() => setPdfZoom(Math.min(2.5, pdfZoom + 0.2))}
                    disabled={pdfZoom >= 2.5}
                    style={{
                      padding: '4px 12px',
                      background: pdfZoom >= 2.5 ? '#ccc' : '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: pdfZoom >= 2.5 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    +
                  </button>
                  <button
                    onClick={() => setPdfZoom(1.8)}
                    style={{
                      padding: '4px 12px',
                      background: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                    title="Reset zoom to 160%"
                  >
                    Reset
                  </button>
                </div>

                {/* Page Navigation Controls */}
                {numPages && (
                  <div style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1000,
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }}>
                    <button
                      onClick={goToPrevPage}
                      disabled={pageNumber <= 1}
                      style={{
                        padding: '6px 12px',
                        background: pageNumber <= 1 ? '#ccc' : '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: pageNumber <= 1 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      ← Prev
                    </button>
                    <span style={{ fontSize: '14px', fontWeight: '600' }}>
                      Page {pageNumber} of {numPages}
                    </span>
                    <button
                      onClick={goToNextPage}
                      disabled={pageNumber >= numPages}
                      style={{
                        padding: '6px 12px',
                        background: pageNumber >= numPages ? '#ccc' : '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: pageNumber >= numPages ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Next →
                    </button>
                  </div>
                )}

                {/* PDF Viewer using react-pdf */}
                <div style={{
                  flex: 1,
                  overflow: 'auto',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  padding: '20px',
                  background: '#f5f5f5'
                }}>
                  {pdfError ? (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: '#d32f2f'
                    }}>
                      <p style={{ fontSize: '18px', marginBottom: '10px' }}>❌ Error loading PDF</p>
                      <p style={{ fontSize: '14px' }}>{pdfError.message || 'Unable to load PDF file'}</p>
                    </div>
                  ) : (
                    <Document
                      file={topicUrl}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={onDocumentLoadError}
                      loading={
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%'
                        }}>
                          <p style={{ fontSize: '18px' }}>Loading PDF...</p>
                        </div>
                      }
                    >
                      <Page
                        pageNumber={pageNumber}
                        scale={pdfZoom}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                      />
                    </Document>
                  )}
                </div>
              </div>
            ) : (
              <iframe
                src={topicUrl}
                title="Topic Content"
                style={{ width: '100%', height: '100%', border: 'none', borderRadius: '16px' }}
                sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              />
            )
          ) : isEmpty ? (
            <div className="empty-state">
              <div className="empty-icon">📄</div>
              <h3>Content to Display</h3>
            </div>
          ) : file ? (
            <div className="content-container file-preview-container">
              <div className="content-header">
                <h3>Uploaded File</h3>
                <span className="content-type">{file.type || 'Unknown Type'}</span>
              </div>
              {/* Image Preview */}
              {file.type.startsWith('image/') && filePreview && (
                <img src={filePreview} alt={file.name} className="file-image-preview fit-preview" />
              )}
              {/* DOCX Preview */}
              {(file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || file.name.endsWith('.docx')) && fileText && (
                <div className="docx-preview fit-preview" dangerouslySetInnerHTML={{ __html: fileText }} />
              )}
              {/* Text Preview */}
              {file.type.startsWith('text/') && fileText && (
                <pre className="content-text file-text-preview fit-preview">{fileText}</pre>
              )}
              {/* Fallback */}
              {!filePreview && !fileText && (
                <div className="file-info">
                  <span role="img" aria-label="file">📄</span> {file.name}
                </div>
              )}
            </div>
          ) : (
            <div className="content-container">
              <div className="content-header">
                <h3>Generated Content</h3>
                <span className="content-type">JSON Format</span>
              </div>
              <pre className="content-text">{content}</pre>
            </div>
          )}
        </div>
        <div className="content-buttons">
          <div className="circular-timer-container">
            <svg className="circular-timer-svg" viewBox="0 0 200 200">
              {/* Background circle */}
              <circle
                cx="100"
                cy="100"
                r="85"
                fill="#2e4142"
                stroke="none"
              />
              {/* Progress arc with gradient */}
              <defs>
                <linearGradient id="timerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style={{ stopColor: '#ff6b35', stopOpacity: 1 }} />
                  <stop offset="50%" style={{ stopColor: '#f7931e', stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: '#ffd700', stopOpacity: 0.3 }} />
                </linearGradient>
              </defs>
              <circle
                cx="100"
                cy="100"
                r="85"
                fill="none"
                stroke="url(#timerGradient)"
                strokeWidth="12"
                strokeDasharray={`${(timeLeft / SESSION_SECONDS) * 534} 534`}
                strokeLinecap="round"
                transform="rotate(-90 100 100)"
                style={{ transition: 'stroke-dasharray 1s linear' }}
              />
            </svg>
            <div className="circular-timer-content">
              <div className="timer-label">TIMER</div>
              <div className="timer-time">{formatTime(timeLeft)}</div>
            </div>
          </div>
          {/*}
          <div 
            className="music-icon-button" 
            onClick={handleVibesClick}
            title={selectedMusic ? `Playing: ${selectedMusic.name}` : "Select background music"}
          >
            <img 
              src="/MusicNote.png" 
              alt="Music" 
              className="music-note-icon"
            />
          </div>
          */}
          {/* Avatar Container - maintains space to prevent layout shift */}
            <div className="avatar-container">
          {/* Avatar Section - Animated Overlay */}
          <div 
            id="avatar-overlay"
            className={avatarOverlayLarge ? 'avatar-overlay-large' : 'avatar-overlay-small'}
            style={{
              position: avatarOverlayLarge ? 'fixed' : 'relative',
              background: 'transparent',
              zIndex: avatarOverlayLarge ? 1500 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'auto',
              overflow: 'hidden',
              transition: 'all 1s ease',
              width: avatarOverlayLarge ? '500px' : '250px',
              height: avatarOverlayLarge ? '400px' : '200px',
              top: avatarOverlayLarge ? '25%' : 'auto',
              left: avatarOverlayLarge ? '50%' : 'auto',
              right: avatarOverlayLarge ? 'auto' : 'auto',
              transform: avatarOverlayLarge ? 'translate(-50%, -50%)' : 'none',
              borderRadius: avatarOverlayLarge ? '32px' : '24px',
              marginTop: avatarOverlayLarge ? '0' : '0'
            }}
          >
            <NebulaOrb liveKitRoomRef={liveKitRoomRef} liveKitConnectionState={liveKitConnectionState} />
          </div>
        </div>
          
          {/* Mute Button - positioned right under the avatar */}
          <button
            onClick={onToggleMute}
            disabled={!liveKitRoomRef?.current || liveKitConnectionState !== ConnectionState.Connected}
            style={{
              marginTop: '1px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              border: 'none',
              borderRadius: '8px',
              cursor: (!liveKitRoomRef?.current || liveKitConnectionState !== ConnectionState.Connected) ? 'not-allowed' : 'pointer',
              background: (!liveKitRoomRef?.current || liveKitConnectionState !== ConnectionState.Connected) 
                ? '#666' 
                : (isMicrophoneMuted ? '#4A6B70' : '#6A8B90'),
              color: 'white',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s',
              alignSelf: 'center',
              opacity: (!liveKitRoomRef?.current || liveKitConnectionState !== ConnectionState.Connected) ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              minWidth: '180px',
              height: '40px'
            }}
            onMouseEnter={(e) => {
              if (liveKitRoomRef?.current && liveKitConnectionState === ConnectionState.Connected) {
                e.target.style.opacity = '0.9';
                e.target.style.transform = 'scale(1.02)';
              }
            }}
            onMouseLeave={(e) => {
              if (liveKitRoomRef?.current && liveKitConnectionState === ConnectionState.Connected) {
                e.target.style.opacity = '1';
                e.target.style.transform = 'scale(1)';
              }
            }}
          >
            {isMicrophoneMuted ? (
              'Ask Me a Question'
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '20px' }}>
                <div className="waveform-bar" style={{ animationDelay: '0s' }}></div>
                <div className="waveform-bar" style={{ animationDelay: '0.1s' }}></div>
                <div className="waveform-bar" style={{ animationDelay: '0.2s' }}></div>
                <div className="waveform-bar" style={{ animationDelay: '0.3s' }}></div>
                <div className="waveform-bar" style={{ animationDelay: '0.4s' }}></div>
                <div className="waveform-bar" style={{ animationDelay: '0.5s' }}></div>
                <div className="waveform-bar" style={{ animationDelay: '0.6s' }}></div>
              </div>
            )}
          </button>

          {/* Quiz Me button — manual mode only.
              Each click fires the next quiz in the sequence (individual x5 then
              final); after the final it loops back to the first. */}
          {quizMode === 'manual' && (
            <button
              className="app-btn"
              onClick={onManualQuizTrigger}
              style={{
                marginTop: '8px',
                padding: '10px 16px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                background: '#6A8B90',
                color: 'white',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              }}
            >
              Quiz Me
            </button>
          )}

          {/* Extra Quiz button — timed mode. Lets the student take an on-demand quiz on
              top of the schedule; locked shortly before each scheduled quiz so the two
              don't collide. */}
          {quizMode === 'timed' && QUIZ_CONFIG.extraQuiz.enabled && onExtraQuiz && isTimerActive && nextScheduledInSec !== null && (
            <>
              <button
                className="app-btn"
                onClick={onExtraQuiz}
                disabled={extraQuizLocked || quizOpen}
                style={{
                  marginTop: '8px',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: extraQuizLocked || quizOpen ? 'not-allowed' : 'pointer',
                  background: '#6A8B90',
                  color: 'white',
                  fontWeight: 600,
                  opacity: extraQuizLocked || quizOpen ? 0.55 : 1,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                }}
              >
                Extra Quiz
              </button>
              {extraQuizLocked && (
                <div style={{ marginTop: '4px', fontSize: '13px', color: '#555' }}>
                  Next quiz in {formatTime(nextScheduledInSec)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Quiz Preparation Message - 5 seconds before quiz */}
      {showQuizPrepMessage && (
        <div className="quiz-popup-overlay">
          <div className="quiz-popup" style={{
            background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
            border: '3px solid #81C784',
            boxShadow: '0 4px 20px rgba(129, 199, 132, 0.3)'
          }}>
            <h3 style={{ color: '#2E7D32', fontSize: '24px', margin: '0 0 15px 0' }}>📚</h3>
            <p style={{ color: '#1B5E20', fontSize: '20px', fontWeight: '600', margin: 0 }}>
              Let's check your knowledge now!
            </p>
          </div>
        </div>
      )}

      {/* Quiz Popup */}
      {showQuizPopup && (
        <div className="quiz-popup-overlay">
          <div className="quiz-popup">
            <h3>Quiz Reminder</h3>
            <p>You have a quiz to attend</p>
            <button className="quiz-popup-close" onClick={closeQuizPopup}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Music Selection Popup */}
      {showMusicPopup && (
        <div className="music-selection-overlay">
          <div className="music-selection-popup">
            <h3>Select Background Music</h3>
            
            {/* Currently Playing Section */}
            {selectedMusic && (
              <div className="currently-playing">
                <h4>Currently Playing</h4>
                <div className="current-track">
                  <span className="current-track-icon">{selectedMusic.icon}</span>
                  <div className="current-track-info">
                    <span className="current-track-name">{selectedMusic.name}</span>
                    <span className="current-track-status">{isPlaying ? 'Playing' : 'Paused'}</span>
                  </div>
                  <div className="current-track-controls">
                    <button className="track-control-btn" onClick={toggleAudio}>
                      {isPlaying ? '⏸️' : '▶️'}
                    </button>
                    <button className="track-control-btn" onClick={stopAudio}>
                      ⏹️
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="music-options">
              {backgroundMusic.map(music => (
                <div 
                  key={music.id} 
                  className={`music-option ${selectedMusic?.id === music.id ? 'active' : ''}`}
                  onClick={() => selectMusic(music)}
                >
                  <span className="music-icon">{music.icon}</span>
                  <span className="music-name">{music.name}</span>
                  <span className="music-description">{music.description}</span>
                  {selectedMusic?.id === music.id && (
                    <span className="playing-indicator">♪</span>
                  )}
                </div>
              ))}
            </div>
            <button className="music-selection-close" onClick={closeMusicPopup}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RightSection;